import {
  IStackStyles,
  ITextFieldStyles,
  Pivot,
  PivotItem,
  Stack,
  TextField,
} from '@fluentui/react';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { getSettings, ISettings } from '../../services/settingsService';
import AppContext from '../AppContext';
import References from './References';
import Statuses from './Statuses';
import Labels from './Labels';
import Reports from './Reports';
import OtherSettings from './OtherSettings';
import Pricing from './Pricing';
import { legacyPricingRedirect } from './pricingSettings';
import { FeatureFlag, Features } from '@/libs/featureAccess';

const settingsTabTitle: Record<string, string> = {
  client: 'Client',
  references: 'References',
  workStatuses: 'Work Statuses',
  assetStatuses: 'Asset Statuses',
  orderStatuses: 'Order Statuses',
  labels: 'Labels',
  reports: 'Dashboards',
  pricing: 'Pricing',
  other: 'Global settings',
};

const Settings = () => {
  let { tab } = useParams();

  const navigate = useNavigate();
  const { search } = useLocation();

  const [clientUrl, setClientUrl] = useState('');

  useEffect(() => {
    const target =
      tab === 'pricing' ? legacyPricingRedirect(search) : undefined;
    if (target) navigate(target, { replace: true });
  }, [tab, search, navigate]);

  const context = useContext(AppContext);

  useEffect(() => {
    context.setSelectedTab(settingsTabTitle[tab ?? 'references'] ?? 'Settings');
    const abortController = new AbortController();

    getSettings(abortController).then((data: ISettings) => {
      setClientUrl(data?.clientUrl);
    });

    return () => {
      abortController.abort();
    };
  }, [tab]);

  const textFieldStyles: Partial<ITextFieldStyles> = {
    fieldGroup: { width: 300 },
    root: { padding: 10 },
  };

  const stackStyles: IStackStyles = {
    root: {
      label: {
        display: 'flex',
        paddingLeft: 10,
      },
    },
  };

  return (
    <Stack styles={stackStyles} horizontalAlign="stretch">
      <Pivot
        defaultSelectedKey={tab ?? 'references'}
        onLinkClick={(item) => {
          if (item?.props.itemKey === 'back') {
            navigate('/');
          } else {
            navigate(`/settings/${item?.props.itemKey}`);
          }
        }}
      >
        <PivotItem itemIcon="ChromeBack" itemKey="back" />
        <FeatureFlag featureKey={Features.FEATURE_ISSUES}>
          <PivotItem headerText="Client" itemKey="client">
            <TextField
              label="ClientUrl"
              value={clientUrl}
              styles={textFieldStyles}
              readOnly
            />
          </PivotItem>
        </FeatureFlag>
        <PivotItem headerText="References" itemKey="references">
          <References />
        </PivotItem>
        <PivotItem headerText="Work Statuses" itemKey="workStatuses">
          <Statuses entityType="work" />
        </PivotItem>
        <PivotItem headerText="Asset Statuses" itemKey="assetStatuses">
          <Statuses entityType="asset" />
        </PivotItem>
        <PivotItem headerText="Order Statuses" itemKey="orderStatuses">
          <Statuses entityType="order" />
        </PivotItem>
        <PivotItem headerText="Labels" itemKey="labels">
          <Labels />
        </PivotItem>
        <PivotItem headerText="Dashboards" itemKey="reports">
          <Reports />
        </PivotItem>
        <PivotItem headerText="Pricing" itemKey="pricing">
          <Pricing />
        </PivotItem>
        <PivotItem headerText="Global settings" itemKey="other">
          <OtherSettings />
        </PivotItem>
      </Pivot>
    </Stack>
  );
};

export default Settings;
